"use client";

//
// EditProfileView.tsx — Web counterpart of iOS `EditProfileView.swift`.
//
// Mirrors the SwiftUI structure:
//   - profileHeaderSection (3:4 background + 100×100 avatar with 4px white
//     border + 32×32 accent camera FAB + "Change Background" capsule)
//   - formSection (DSGlassCard with Display Name TextField + Divider + Bio
//     TextEditor with 100-grapheme counter)
//   - selectedImagesPreview (pending avatar/background thumbnails)
//
// PhotosPicker is mapped to `<input type="file" accept="image/*">` wrapped in
// a `<label>` so the tap target stays native HTML. All colors resolve through
// DS CSS variables (`var(--ds-*)`); no inline hex.
//

import Link from "@/infrastructure/router/Link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	Avatar,
	EmptyState,
	GlassButton,
	GlassCard,
	GlassIconButton,
	GlassTextEditor,
	GlassTextField,
	NavigationBar,
	Skeleton,
	Toast,
} from "@/components/design-system/ArtistDesignSystem";
import {
	browserSocialRepository,
	type ProfileDraft,
	type ProfileRevision,
	type SocialRepository,
	sameScope,
	scopeKey,
} from "@/domain/social/SocialRepository";
import { artistSupabase } from "@/infrastructure/supabase/client";

// ─────────────────────────────────────────────────────────────────────────────
// Constants — mirrored from SocialHubs so this file is self-contained.
// ─────────────────────────────────────────────────────────────────────────────

const DRAFTS_KEY = "artist.social-drafts.v1";
type DraftMap = Record<string, unknown>;
const graphemeSegmenter = new Intl.Segmenter(undefined, {
	granularity: "grapheme",
});

interface PendingProfileObject {
	readonly path: string;
	readonly kind: "avatar" | "profile_background";
	readonly signedUrl: string;
}

const PROFILE_MEDIA_SELECT = [
	"id",
	"display_name",
	"avatar_url",
	"avatar_pending_url",
	"background_url",
	"background_pending_url",
	"bio",
	"updated_at",
].join(",");

async function uploadPendingProfileImage(
	ownerUserId: string,
	kind: PendingProfileObject["kind"],
	file: File,
): Promise<PendingProfileObject> {
	const prefix = kind === "avatar" ? "avatar" : "background";
	const path = `${ownerUserId}/${prefix}_${crypto.randomUUID().toLowerCase()}.jpg`;
	const storage = artistSupabase().storage.from("avatars-pending");
	const { error: uploadError } = await storage.upload(path, file, {
		contentType: "image/jpeg",
		cacheControl: "0",
		upsert: false,
	});
	if (uploadError) throw uploadError;
	const { data, error: signError } = await storage.createSignedUrl(
		path,
		60 * 60 * 24 * 7,
	);
	if (signError || !data.signedUrl) {
		await storage.remove([path]);
		throw signError ?? new Error("profile-media-sign-failed");
	}
	return { path, kind, signedUrl: data.signedUrl };
}

async function removePendingProfileObjects(
	objects: readonly PendingProfileObject[],
): Promise<void> {
	if (objects.length === 0) return;
	await artistSupabase()
		.storage.from("avatars-pending")
		.remove(objects.map((item) => item.path));
}

// ─────────────────────────────────────────────────────────────────────────────
// Text helpers — bounded to grapheme clusters (mirrors iOS `.count` semantics).
// ─────────────────────────────────────────────────────────────────────────────

export function graphemeCount(value: string): number {
	return Array.from(graphemeSegmenter.segment(value)).length;
}

async function prepareProfileJpeg(file: File, filename: string): Promise<File> {
	if (!file.type.startsWith("image/") || file.size < 1 || file.size > 40 * 1024 * 1024) {
		throw new Error("profile-image-invalid");
	}
	const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
	try {
		const maximumDimension = 4096;
		const ratio = Math.min(
			1,
			maximumDimension / Math.max(bitmap.width, bitmap.height),
		);
		const width = Math.max(1, Math.round(bitmap.width * ratio));
		const height = Math.max(1, Math.round(bitmap.height * ratio));
		const canvas = document.createElement("canvas");
		canvas.width = width;
		canvas.height = height;
		const context = canvas.getContext("2d", { alpha: false });
		if (context === null) throw new Error("profile-image-canvas-unavailable");
		context.drawImage(bitmap, 0, 0, width, height);
		const blob = await new Promise<Blob>((resolve, reject) => {
			canvas.toBlob(
				(value) => {
					if (value === null) reject(new Error("profile-image-encode-failed"));
					else resolve(value);
				},
				"image/jpeg",
				0.8,
			);
		});
		if (blob.size < 1 || blob.size > 10 * 1024 * 1024) {
			throw new Error("profile-image-encoded-size-invalid");
		}
		return new File([blob], filename, {
			type: "image/jpeg",
			lastModified: Date.now(),
		});
	} finally {
		bitmap.close();
	}
}

function boundedText(value: string, maxGraphemes: number): string {
	return Array.from(graphemeSegmenter.segment(value), ({ segment }) => segment)
		.slice(0, maxGraphemes)
		.join("");
}

// ─────────────────────────────────────────────────────────────────────────────
// Local draft persistence (mirrors iOS `UserDefaults` background URL cache).
// ─────────────────────────────────────────────────────────────────────────────

function readDraft<T>(key: string, fallback: T): T {
	if (typeof window === "undefined") return fallback;
	try {
		const record = JSON.parse(
			window.localStorage.getItem(DRAFTS_KEY) ?? "{}",
		) as DraftMap;
		return key in record ? (record[key] as T) : fallback;
	} catch {
		return fallback;
	}
}

function writeDraft(key: string, value: unknown): void {
	try {
		const record = JSON.parse(
			window.localStorage.getItem(DRAFTS_KEY) ?? "{}",
		) as DraftMap;
		record[key] = value;
		window.localStorage.setItem(DRAFTS_KEY, JSON.stringify(record));
	} catch {
		// The mounted state remains authoritative when persistent storage is unavailable.
	}
}

function clearDraft(key: string): void {
	try {
		const record = JSON.parse(
			window.localStorage.getItem(DRAFTS_KEY) ?? "{}",
		) as DraftMap;
		delete record[key];
		window.localStorage.setItem(DRAFTS_KEY, JSON.stringify(record));
	} catch {
		// No visible state is derived from a failed cleanup.
	}
}

// ─────────────────────────────────────────────────────────────────────────────
// Scope + fence — mirrors the iOS `currentProfile` owner guard so a stale
// owner/scope never receives a saved acknowledgement for another user.
// ─────────────────────────────────────────────────────────────────────────────

function useScope(): {
	readonly scope: ReturnType<typeof useArtistSession>["scope"];
	readonly ready: boolean;
} {
	const auth = useArtistSession();
	return { scope: auth.scope, ready: auth.phase === "user" };
}

function ScopeGate({ children }: Readonly<{ children: React.ReactNode }>) {
	const { ready, scope } = useScope();
	if (ready && scope !== null)
		return <ScopedSurface key={scopeKey(scope)}>{children}</ScopedSurface>;
	return (
		<section className="social-gate">
			<NavigationBar title="Artist" />
			<EmptyState
				title="登录后继续"
				message="你的草稿、资料和会话会保存在自己的账户中。"
				symbol={<ArtistIcon name="person" size={40} />}
			/>
			<Link className="primary-action" href="/auth">
				登录
			</Link>
		</section>
	);
}

function ScopedSurface({ children }: Readonly<{ children: React.ReactNode }>) {
	return <>{children}</>;
}

function useFence() {
	const scope = useArtistSession().scope;
	const current = useRef(scope);
	const generations = useRef(new Map<string, number>());
	useEffect(() => {
		current.current = scope;
		generations.current.clear();
		return () => {
			current.current = null;
			generations.current.clear();
		};
	}, [scope]);
	return useCallback((requestKey: string) => {
		const expectedScope = current.current;
		const expectedGeneration = (generations.current.get(requestKey) ?? 0) + 1;
		generations.current.set(requestKey, expectedGeneration);
		return () =>
			sameScope(current.current, expectedScope) &&
			generations.current.get(requestKey) === expectedGeneration;
	}, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared UI primitives — Notice (Toast), ConflictCard, SocialLoading.
// ─────────────────────────────────────────────────────────────────────────────

function Notice({
	children,
	tone = "info",
}: Readonly<{
	children: React.ReactNode;
	tone?: "info" | "error" | "success";
}>) {
	return <Toast tone={tone}>{children}</Toast>;
}

function ConflictCard({
	title,
	summary,
	onReload,
	onKeep,
}: Readonly<{
	title: string;
	summary: string;
	onReload: () => void;
	onKeep: () => void;
}>) {
	return (
		<GlassCard className="social-conflict" role="alert">
			<strong>{title}</strong>
			<p>{summary}</p>
			<div>
				<GlassButton size="sm" onClick={onReload}>
					重新载入远端
				</GlassButton>
				<GlassButton size="sm" onClick={onKeep}>
					继续编辑
				</GlassButton>
			</div>
		</GlassCard>
	);
}

function SocialLoading({ title }: Readonly<{ title: string }>) {
	return (
		<section className="social-loading" aria-busy="true">
			<NavigationBar title="Artist" />
			<Skeleton rows={4} />
			<p className="sr-only">{title}</p>
		</section>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// ProfileEditHub — public entry. Wraps the editor in the scope gate so the
// draft is always owner-scoped before any input mounts.
// ─────────────────────────────────────────────────────────────────────────────

export function ProfileEditHub({
	repository = browserSocialRepository,
}: Readonly<{ repository?: SocialRepository }>) {
	return (
		<ScopeGate>
			<ProfileEditor repository={repository} />
		</ScopeGate>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// ProfileEditor — the actual surface. Matches iOS `EditProfileView`:
//   1. NavigationBar with trailing save (checkmark), disabled when invalid.
//   2. profileHeaderSection: 3:4 background image + "Change Background"
//      capsule + 100×100 avatar with 32×32 accent camera FAB.
//   3. formSection: GlassCard with Display Name (leading person icon) +
//      Divider + Bio (TextEditor, 100-grapheme counter).
//   4. selectedImagesPreview: pending avatar/background thumbnails.
//   5. Alerts mapped to Toast notices (success / error) + ConflictCard.
// ─────────────────────────────────────────────────────────────────────────────

function ProfileEditor({
	repository,
}: Readonly<{ repository: SocialRepository }>) {
	const { scope } = useScope();
	const fence = useFence();
	const [draft, setDraft] = useState<ProfileDraft | null>(null);
	const [revision, setRevision] = useState<ProfileRevision | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [status, setStatus] = useState<string | null>(null);
	const [conflict, setConflict] = useState<string | null>(null);
	const [reloadGeneration, setReloadGeneration] = useState(0);
	const [avatarFile, setAvatarFile] = useState<File | null>(null);
	const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
	const [avatarPreviewURL, setAvatarPreviewURL] = useState<string | null>(null);
	const [backgroundPreviewURL, setBackgroundPreviewURL] = useState<string | null>(null);
	const [serverAvatarURL, setServerAvatarURL] = useState<string | null>(null);
	const [serverBackgroundURL, setServerBackgroundURL] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		void reloadGeneration;
		if (!scope) return;
		const applies = fence("profile.load");
		setDraft(null);
		setRevision(null);
		setError(null);
		repository
			.loadProfile(scope)
			.then((loaded) => {
				if (!applies()) return;
				const restored = readDraft(`profile:${scopeKey(scope)}`, loaded.value);
				setDraft({
					...restored,
					avatar: "none",
					background: "none",
					avatarURL: loaded.value.avatarURL ?? null,
					backgroundURL: loaded.value.backgroundURL ?? null,
				});
				setServerAvatarURL(loaded.value.avatarURL ?? null);
				setServerBackgroundURL(loaded.value.backgroundURL ?? null);
				setRevision(loaded.revision);
			})
			.catch(() => applies() && setError("个人资料加载失败，请稍后重试。"));
	}, [fence, reloadGeneration, repository, scope]);

	useEffect(() => {
		return () => {
			if (avatarPreviewURL !== null) URL.revokeObjectURL(avatarPreviewURL);
			if (backgroundPreviewURL !== null) URL.revokeObjectURL(backgroundPreviewURL);
		};
	}, [avatarPreviewURL, backgroundPreviewURL]);

	if (!scope || !draft || revision === null)
		return <SocialLoading title="正在载入个人资料…" />;
	const activeScope = scope;
	const activeDraft = draft;
	const activeRevision = revision;
	const invalid = draft.displayName.trim().length === 0;
	const update = (next: ProfileDraft) => {
		setDraft(next);
		writeDraft(`profile:${scopeKey(activeScope)}`, {
			...next,
			avatar: avatarFile === null ? next.avatar : "none",
			background: backgroundFile === null ? next.background : "none",
			avatarURL: avatarFile === null ? next.avatarURL : serverAvatarURL,
			backgroundURL: backgroundFile === null ? next.backgroundURL : serverBackgroundURL,
		});
		setStatus(null);
	};

	async function selectMedia(kind: "avatar" | "background", file: File): Promise<void> {
		setError(null);
		try {
			const prepared = await prepareProfileJpeg(file, `${kind}.jpg`);
			const previewURL = URL.createObjectURL(prepared);
			if (kind === "avatar") {
				if (avatarPreviewURL !== null) URL.revokeObjectURL(avatarPreviewURL);
				setAvatarFile(prepared);
				setAvatarPreviewURL(previewURL);
				setDraft({ ...activeDraft, avatar: "pending", avatarURL: previewURL });
			} else {
				if (backgroundPreviewURL !== null) URL.revokeObjectURL(backgroundPreviewURL);
				setBackgroundFile(prepared);
				setBackgroundPreviewURL(previewURL);
				setDraft({ ...activeDraft, background: "pending", backgroundURL: previewURL });
			}
			setStatus(null);
		} catch {
			setError("图片无法处理。请选择有效图片并确保文件大小合理。");
		}
	}

	function clearMedia(kind: "avatar" | "background"): void {
		if (kind === "avatar") {
			if (avatarPreviewURL !== null) URL.revokeObjectURL(avatarPreviewURL);
			setAvatarFile(null);
			setAvatarPreviewURL(null);
			setDraft({ ...activeDraft, avatar: "none", avatarURL: serverAvatarURL });
		} else {
			if (backgroundPreviewURL !== null) URL.revokeObjectURL(backgroundPreviewURL);
			setBackgroundFile(null);
			setBackgroundPreviewURL(null);
			setDraft({ ...activeDraft, background: "none", backgroundURL: serverBackgroundURL });
		}
	}

	async function save(): Promise<void> {
		if (invalid || isSaving) {
			setError("显示名称不能为空。");
			return;
		}
		const applies = fence("profile.save");
		setError(null);
		setIsSaving(true);
		const uploaded: PendingProfileObject[] = [];
		try {
			if (avatarFile !== null || backgroundFile !== null) {
				if (avatarFile !== null) {
					uploaded.push(
						await uploadPendingProfileImage(
							activeScope.ownerUserId,
							"avatar",
							avatarFile,
						),
					);
				}
				if (backgroundFile !== null) {
					uploaded.push(
						await uploadPendingProfileImage(
							activeScope.ownerUserId,
							"profile_background",
							backgroundFile,
						),
					);
				}
				const avatar = uploaded.find((item) => item.kind === "avatar") ?? null;
				const background =
					uploaded.find((item) => item.kind === "profile_background") ?? null;
				const { data, error: updateError } = await artistSupabase()
					.from("profiles")
					.update({
						display_name: activeDraft.displayName.trim(),
						bio: activeDraft.bio,
						...(avatar === null
							? {}
							: { avatar_pending_url: avatar.signedUrl }),
						...(background === null
							? {}
							: { background_pending_url: background.signedUrl }),
					})
					.eq("id", activeScope.ownerUserId)
					.eq("updated_at", String(activeRevision))
					.select(PROFILE_MEDIA_SELECT)
					.maybeSingle();
				if (!applies()) return;
				if (updateError) throw updateError;
				if (data === null) {
					await removePendingProfileObjects(uploaded);
					setConflict("资料已在其他设备更新。");
					return;
				}
				const row = data as unknown as Record<string, unknown>;
				if (
					typeof row.display_name !== "string" ||
					typeof row.updated_at !== "string"
				) throw new Error("profile-media-response-invalid");
				const value: ProfileDraft = {
					displayName: row.display_name,
					bio: typeof row.bio === "string" ? row.bio : "",
					avatar: "none",
					background: "none",
					avatarURL:
						typeof row.avatar_pending_url === "string"
							? row.avatar_pending_url
							: typeof row.avatar_url === "string"
								? row.avatar_url
								: null,
					backgroundURL:
						typeof row.background_pending_url === "string"
							? row.background_pending_url
							: typeof row.background_url === "string"
								? row.background_url
								: null,
				};
				if (avatarPreviewURL !== null) URL.revokeObjectURL(avatarPreviewURL);
				if (backgroundPreviewURL !== null) URL.revokeObjectURL(backgroundPreviewURL);
				setAvatarFile(null);
				setBackgroundFile(null);
				setAvatarPreviewURL(null);
				setBackgroundPreviewURL(null);
				setServerAvatarURL(value.avatarURL ?? null);
				setServerBackgroundURL(value.backgroundURL ?? null);
				setDraft(value);
				setRevision(row.updated_at);
				clearDraft(`profile:${scopeKey(activeScope)}`);
				setStatus("个人资料与图片已提交服务端审核并同步。");
				return;
			}

			const result = await repository.saveProfile(activeScope, activeDraft, activeRevision);
			if (!applies()) return;
			if (result.kind === "conflict") setConflict(result.remoteSummary);
			else if (result.kind === "capabilityUnavailable")
				setStatus("资料尚未同步，当前草稿已保留。请检查登录或网络后重试。");
			else {
				setRevision(result.value.revision);
				clearDraft(`profile:${scopeKey(activeScope)}`);
				setStatus("个人资料已同步。");
			}
		} catch {
			await removePendingProfileObjects(uploaded).catch(() => undefined);
			if (applies()) setError("个人资料同步失败，请稍后重试。");
		} finally {
			if (applies()) setIsSaving(false);
		}
	}

	function reloadRemote(): void {
		clearDraft(`profile:${scopeKey(activeScope)}`);
		setConflict(null);
		setStatus(null);
		setReloadGeneration((value) => value + 1);
	}

	return (
		<section className="social-page profile-edit-page" aria-label="编辑资料">
			<NavigationBar
				title="编辑资料"
				backHref="/settings"
				trailing={
					<GlassIconButton
						label="保存资料"
						type="button"
						disabled={invalid || isSaving}
						onClick={() => void save()}
					>
						<ArtistIcon name="check" size={28} />
					</GlassIconButton>
				}
			/>
			{error ? <Notice tone="error">{error}</Notice> : null}
			{status ? <Notice tone="success">{status}</Notice> : null}
			{conflict ? (
				<ConflictCard
					title="资料已在其他设备更新"
					summary={conflict}
					onReload={reloadRemote}
					onKeep={() => setConflict(null)}
				/>
			) : null}

			<section className="profile-media" aria-label="头像与背景">
				<div className="profile-media__background">
					{draft.backgroundURL ? (
						// biome-ignore lint/performance/noImgElement: authenticated profile media may be an approved remote HTTPS URL.
						<img src={draft.backgroundURL} alt="" />
					) : null}
					<div className="profile-media__overlay" aria-hidden="true" />
					<label className="profile-media__change-bg">
						<input
							type="file"
							accept="image/*"
							aria-label="更换背景"
							className="sr-only"
							onChange={(event) => {
								const file = event.currentTarget.files?.[0];
								if (file !== undefined) void selectMedia("background", file);
							}}
						/>
						<ArtistIcon name="camera" size={24} />
						<span>更换背景</span>
					</label>
				</div>
				<span className="profile-avatar-wrap">
					<Avatar
						name={draft.displayName}
						{...(draft.avatarURL ? { src: draft.avatarURL } : {})}
						size={100}
					/>
					<label className="profile-avatar-fab" aria-label="更换头像">
						<input
							type="file"
							accept="image/*"
							aria-label="更换头像"
							className="sr-only"
							onChange={(event) => {
								const file = event.currentTarget.files?.[0];
								if (file !== undefined) void selectMedia("avatar", file);
							}}
						/>
						<ArtistIcon name="camera" size={16} />
					</label>
				</span>
			</section>

			<form
				className="profile-form"
				onSubmit={(event) => {
					event.preventDefault();
					void save();
				}}
			>
				<GlassCard>
					<GlassTextField
						label="显示名称"
						leading={<ArtistIcon name="person" />}
						value={draft.displayName}
						onChange={(event) =>
							update({ ...draft, displayName: event.currentTarget.value })
						}
					/>
					<div className="ds-form-divider" />
					<GlassTextEditor
						label="个人简介"
						counter={`${graphemeCount(draft.bio)}/100`}
						value={draft.bio}
						onChange={(event) =>
							update({
								...draft,
								bio: boundedText(event.currentTarget.value, 100),
							})
						}
					/>
				</GlassCard>

				{draft.avatar === "pending" || draft.background === "pending" ? (
					<section className="profile-pending" aria-label="待上传图片">
						<h2>待上传图片</h2>
						<div className="profile-pending__list">
							{draft.avatar === "pending" ? (
								<PendingImageItem
									label="头像"
									onRemove={() => clearMedia("avatar")}
								/>
							) : null}
							{draft.background === "pending" ? (
								<PendingImageItem
									label="背景"
									onRemove={() => clearMedia("background")}
								/>
							) : null}
						</div>
					</section>
				) : null}
			</form>
		</section>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// PendingImageItem — 60×60 thumbnail with a circular remove control. Mirrors
// iOS `imagePreviewItem(image:label:onRemove:)`.
// ─────────────────────────────────────────────────────────────────────────────

function PendingImageItem({
	label,
	onRemove,
}: Readonly<{ label: string; onRemove: () => void }>) {
	return (
		<figure className="profile-pending__item">
			<span className="profile-pending__thumb">
				<ArtistIcon name="image" size={22} />
				<button
					type="button"
					className="profile-pending__remove"
					aria-label={`移除${label}`}
					onClick={onRemove}
				>
					<ArtistIcon name="close" size={12} />
				</button>
			</span>
			<figcaption className="profile-pending__label">{label}</figcaption>
		</figure>
	);
}
